<template>
  <div class="bg-white border border-gray-200 rounded-lg overflow-hidden">
    <div class="px-3 py-2 border-b border-gray-200 flex items-center justify-between">
      <h3 class="text-xs font-semibold text-gray-900">Performance vs. Prior Year</h3>
      <span v-if="loading" class="text-[10px] text-gray-400">Loading...</span>
      <span v-else-if="practiceCount" class="text-[10px] text-gray-400">{{ practiceCount }} practices · {{ formatLives(totalLives) }} lives</span>
    </div>
    <div v-if="!loading && chartData" class="h-[240px]">
      <Chart_V5
        :key="chartKey"
        :chart_data="chartData"
        :chart_config="chartConfig"
        override-height="h-[240px]"
      />
    </div>
    <div v-else-if="!loading" class="px-3 py-8 text-center text-gray-400 text-xs">No trajectory data available.</div>
  </div>
</template>

<script>
import axios from 'axios'
import Chart_V5 from '@/components/widgetcomponents/Viz_Elements/Chart_V5.vue'

const YEAR_COLORS = ['#2563EB', '#059669', '#6b7280']

export default {
  name: 'PerformanceTrajectory',
  components: { Chart_V5 },
  props: {
    contractId: { type: String, default: null },
  },
  data() {
    return { loading: false, entries: [], chartKey: 0 }
  },
  computed: {
    practiceCount() {
      return this.entries.length
    },
    totalLives() {
      return this.entries.reduce((sum, e) => sum + (e.panel_size || 0), 0)
    },
    trajectoryByDate() {
      const byDate = {}
      for (const entry of this.entries) {
        const panel = entry.panel_size || 0
        if (panel < 50 || !entry.trajectory?.length) continue
        for (const pt of entry.trajectory) {
          if (!byDate[pt.date]) byDate[pt.date] = { weightedSum: 0, totalPanel: 0 }
          byDate[pt.date].weightedSum += (pt.score ?? pt.percentile ?? 0) * panel
          byDate[pt.date].totalPanel += panel
        }
      }
      return Object.entries(byDate)
        .map(([date, { weightedSum, totalPanel }]) => ({
          date,
          score: totalPanel > 0 ? Math.round(weightedSum / totalPanel) - 50 : 0,
          year: parseInt(date.substring(0, 4)),
          month: parseInt(date.substring(5, 7)),
        }))
        .sort((a, b) => a.date.localeCompare(b.date))
    },
    yearGroups() {
      const groups = {}
      for (const pt of this.trajectoryByDate) {
        if (!groups[pt.year]) groups[pt.year] = []
        groups[pt.year].push(pt)
      }
      return groups
    },
    chartData() {
      const groups = this.yearGroups
      const years = Object.keys(groups).map(Number).sort((a, b) => b - a).slice(0, 2)
      if (!years.length) return null

      // X-axis: month labels Jan-Dec
      const monthLabels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

      const series = years.map((year, idx) => {
        const pts = groups[year]
        const byMonth = {}
        for (const pt of pts) {
          byMonth[pt.month] = pt.score
        }
        return {
          values: monthLabels.map((_, i) => byMonth[i + 1] ?? null),
          config: {
            label: String(year),
            backgroundColor: YEAR_COLORS[idx] || YEAR_COLORS[2],
            type: 'line',
            yAxis: 'left',
          }
        }
      })

      return { labels: monthLabels, series }
    },
    yDomain() {
      const allScores = this.trajectoryByDate.map(p => p.score)
      if (!allScores.length) return { min: -10, max: 10 }
      const dataMin = Math.min(...allScores, 0)
      const dataMax = Math.max(...allScores, 0)
      const span = Math.max(dataMax - dataMin, 4)
      const margin = Math.ceil(span * 0.2)
      // Round to nice numbers
      const min = Math.floor((dataMin - margin) / 5) * 5
      const max = Math.ceil((dataMax + margin) / 5) * 5
      return { min, max }
    },
    chartConfig() {
      const { min, max } = this.yDomain
      // Build tick values: always include 0, then symmetric ticks
      const ticks = [min, 0, max].filter((v, i, a) => a.indexOf(v) === i).sort((a, b) => a - b)
      return {
        chartType: 'vertical_bar',
        margin: { top: 16, right: 20, bottom: 30, left: 56 },
        xAxis: { labelRotation: 0, fontSize: 10 },
        yAxes: [{
          position: 'left',
          fontSize: 10,
          max,
          min,
          tickValues: ticks,
          tickFormat: v => v > 0 ? '+' + v : String(v),
        }],
        legend: { display: true, position: 'top' },
        labelDisplayConfig: { maxLabels: 12, minLabelSpacing: 5 },
        formatting: { percentage: false, sig_figs: 0 },
        hideGridLines: true,
      }
    },
  },
  watch: {
    contractId() { this.fetchData() },
  },
  mounted() { this.fetchData() },
  methods: {
    async fetchData() {
      if (!this.contractId) return
      this.loading = true
      try {
        const params = { contract_id: this.contractId }
        const response = await axios.get('/api/personal-analyst/practice-performance-lists/', { params })
        this.entries = response.data?.entries || []
        this.chartKey++
      } catch (error) {
        console.error('Error fetching performance trajectory:', error)
        this.entries = []
      } finally {
        this.loading = false
      }
    },
    formatLives(n) {
      if (!n) return '0'
      return n >= 1000 ? (n / 1000).toFixed(1) + 'k' : n.toString()
    },
  },
}
</script>
